import {ReactNode} from "react";
import {PlusIcon} from "@heroicons/react/20/solid";

type Action =
    | { label: string; onClick: () => void; icon?: ReactNode }
    | { label: string; href: string; icon?: ReactNode };

type Size = "sm" | "md" | "lg";

export type EmptyStateProps = {
    title: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;              // default document/folder-like icon
    action?: Action;               // button or link
    size?: Size;                   // controls spacing and icon size
    align?: "center" | "left";     // default center
    className?: string;            // extra wrapper classes
};

const sizeMap: Record<Size, { icon: string; hgap: string; title: string; desc: string; wrapper: string }> = {
    sm: {icon: "size-8", hgap: "mt-3", title: "text-sm", desc: "text-xs", wrapper: "p-4"},
    md: {icon: "size-12", hgap: "mt-4", title: "text-md font-semibold", desc: "text-md", wrapper: "p-6"},
    lg: {icon: "size-16", hgap: "mt-5", title: "text-lg font-semibold", desc: "text-md", wrapper: "p-8"},
};

export default function EmptyState({
                                       title,
                                       description,
                                       icon,
                                       action,
                                       size = "md",
                                       align = "center",
                                       className = "",
                                   }: EmptyStateProps) {
    const s = sizeMap[size];
    const isCenter = align === "center";

    const defaultIcon = (
        <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
            className={`text-gray-400 ${s.icon} ${isCenter ? "mx-auto" : ""}`}
        >
            <path
                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const content = (
        <div className={`${isCenter ? "text-center" : "text-left"} ${className}`}>
            <div>{icon ?? defaultIcon}</div>
            <h3 className={`${s.hgap} ${s.title} text-blue-900`}>{title}</h3>
            {description && <p className="mt-1 text-gray-500 text-sm">{description}</p>}
            {action && (
                <div className="mt-6">
                    {"onClick" in action ? (
                        <button
                            type="button"
                            onClick={action.onClick}
                            className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-md font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                        >
                            {action.icon ?? <PlusIcon aria-hidden="true" className="mr-1.5 -ml-0.5 size-5"/>}
                            {action.label}
                        </button>
                    ) : (
                        <a
                            href={action.href}
                            className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
                        >
                            {action.icon ?? <PlusIcon aria-hidden="true" className="mr-1.5 -ml-0.5 size-5"/>}
                            {action.label}
                        </a>
                    )}
                </div>
            )}
        </div>
    );

    return <div className={`${s.wrapper}`}>{content}</div>;
}
